import { TableBody, TableCell, TableHead, TableRow, Box, Typography } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'

// components
import { CustomPagination } from '../../common/CustomPagination'
import { CustomTableCell } from '../../common/CustomTableCell'
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'
import { BankReconciliationsListingProps } from '../../../interfaceTypes/props'
import dayjs from 'dayjs'
import ReconciliationsMenu from '../../menus/Reconciliations'
import { Reconciliation, ReconciliationDocumentFileFormat, useUndoReconciliationMutation } from '../../../../generated'
import { Alert } from '../../common/Alert'
import { useMemo, useState } from 'react'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { QUERY_PARAMS } from '../../../constants'
import { useReconciliationDocument } from './useReconciliationDocument'

const ReconciliationsHistoryListing = ({ reconciliations, loading, refetch }: BankReconciliationsListingProps) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { download, isDownloading, pending } = useReconciliationDocument()
  const [showUnReconcileModal, setShowUnReconcileModal] = useState(false)
  const [undoReconciliationId, setUndoReconciliationId] = useState<number>()
  const reconciliationReverted = searchParams.get(QUERY_PARAMS.RECONCILIATION_STATUS) === 'reverted'

  const mostRecentReconciliationId = useMemo(() => {
    if (!reconciliations?.length) return null
    return Math.max(...reconciliations.map(r => r!.id))
  }, [reconciliations])

  const handleRowClick = (id: number) => {
    navigate(`/accounts/bank-reconciliation/history/${id}`)
  }

  const handleUndoReconciliation = () => {
    undoReconciliationId &&
      undoReconciliation({
        variables: {
          id: undoReconciliationId
        }
      })
  }

  const getNextReconciliationIds = (selectedReconciliationId: number) => {
    if (reconciliations?.length) {
      const sorted = [...reconciliations].filter(Boolean).sort((a, b) => a!.id - b!.id)
      const recIds = sorted?.filter(r => r!.id > selectedReconciliationId).map(r => `#${r!.id}`) ?? []
      return recIds?.length ? `(${recIds.join(', ')})` : []
    }
  }

  const [undoReconciliation, { loading: undoReconciliationLoading }] = useUndoReconciliationMutation({
    onError: () => {},
    async onCompleted(data) {
      const { undoReconciliation } = data || {}
      if (undoReconciliation?.response?.status === 200) {
        refetch?.()
        setShowUnReconcileModal(false)
        Alert.success('Reconciliation undone successfully')
      }
    }
  })

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Generation runs as a background job, so the row stays interactive while it works. */}
      {!!pending && (
        <Typography variant="body2" color="text.secondary" sx={{ mx: 2, mb: 1 }}>
          Preparing the {pending.fileFormat === ReconciliationDocumentFileFormat.Pdf ? 'PDF' : 'CSV'} for #
          {pending.reconciliationId} — the download starts automatically when it is ready.
        </Typography>
      )}

      <StickyTable pagination={<CustomPagination loading={loading || false} />} height="360px">
        <TableHead>
          <TableRow hover>
            <TableCell>ID</TableCell>
            <TableCell>Statement Month</TableCell>
            <TableCell>{`${reconciliationReverted ? 'Reverted' : 'Reconciled'} On`}</TableCell>
            <TableCell>Account Name</TableCell>
            <TableCell>Beginning Balance</TableCell>
            <TableCell>Statement Balance</TableCell>
            <TableCell>Cleared Balance</TableCell>
            <TableCell>{`${reconciliationReverted ? 'Reverted' : 'Reconciled'} By`}</TableCell>
            {!reconciliationReverted && <TableCell>Action</TableCell>}
          </TableRow>
        </TableHead>

        {loading ? (
          <TableSkeleton noOfRows={7} noOfCols={reconciliationReverted ? 8 : 9} />
        ) : (
          <TableBody>
            {reconciliations?.map(reconciliation => {
              const {
                id,
                beginningBalance,
                statementBalance,
                clearedBalance,
                reconciledBy,
                createdAt,
                endDate,
                unreconciledAt,
                unreconciledBy,
                account: { name: accountName }
              } = reconciliation!
              const reconciledByUser = reconciledBy?.firstName + ' ' + reconciledBy?.lastName
              const unreconciledByUser = unreconciledBy?.firstName + ' ' + unreconciledBy?.lastName

              return (
                <TableRow key={id} hover>
                  <TableCell onClick={() => id && handleRowClick(id)} sx={{ cursor: 'pointer' }}>
                    <Typography variant="body1" color="primary" sx={{ cursor: 'pointer' }}>
                      #{id}
                    </Typography>
                  </TableCell>
                  {/* Statement period the reconciliation covers. `endDate` is a plain `date` column (already a
                      'YYYY-MM-DD' string), unlike createdAt/unreconciledAt which arrive as epoch millis. */}
                  <CustomTableCell value={endDate ? dayjs(endDate).format('MMMM YYYY') : '--'} />
                  <CustomTableCell value={dayjs(parseInt(unreconciledAt || createdAt)).format('MM/DD/YYYY')} />
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" color="primary">
                        {accountName || '--'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <CustomTableCell value={`$${beginningBalance.toFixed(2)}`} />
                  <CustomTableCell value={`$${statementBalance.toFixed(2)}`} />
                  <CustomTableCell value={`$${clearedBalance.toFixed(2)}`} />
                  <CustomTableCell value={reconciliationReverted ? unreconciledByUser : reconciledByUser} />
                  {!reconciliationReverted && (
                    <TableCell>
                      <ReconciliationsMenu
                        onUnreconcile={() => {
                          setShowUnReconcileModal(true)
                          setUndoReconciliationId(id)
                        }}
                        onDownload={fileFormat => download(id, fileFormat)}
                        canUnreconcile={id === mostRecentReconciliationId}
                        downloading={isDownloading(id)}
                        reconciliation={reconciliation as Reconciliation}
                      />
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        )}
      </StickyTable>

      <ConfirmationModal
        isOpen={showUnReconcileModal}
        title="Confirm Revert Reconciliation"
        message={`Are you sure you want to revert #${undoReconciliationId} reconciliation? Reconciliations ${
          undoReconciliationId && getNextReconciliationIds(undoReconciliationId)
        } before it will be reverted.`}
        confirmText="Revert Reconciliation"
        cancelText="Cancel"
        variant="danger"
        isLoading={undoReconciliationLoading}
        onConfirm={handleUndoReconciliation}
        onCancel={() => setShowUnReconcileModal(false)}
      />
    </Box>
  )
}

export default ReconciliationsHistoryListing
